/**
 * Production Monitoring Dashboard
 * 
 * Real-time monitoring interface with:
 * - Performance metrics visualization
 * - Error tracking and analysis
 * - System health indicators
 * - User interaction analytics
 * - Resource usage monitoring
 * - Alerting and notification management
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Grid,
  Text,
  Badge,
  Group,
  Stack,
  Progress,
  ActionIcon,
  Tooltip,
  Modal,
  Tabs,
  JsonInput,
  Button,
  Alert,
  RingProgress
} from '@mantine/core';
// @mantine/charts not installed - charts disabled for now
// import { LineChart, BarChart } from '@mantine/charts';
import {
  IconActivity,
  IconAlertTriangle,
  IconBug,
  IconClock,
  IconCpu,
  IconDatabase,
  IconDownload,
  IconEye,
  IconNetwork,
  IconRefresh,
  IconSettings,
  IconUsers,
  IconZoomIn
} from '@tabler/icons-react';
import { getPerformanceMonitor, PerformanceMetrics } from './PerformanceMonitor';
import { getLogger } from './Logger';
import { useErrorHandling } from '../error/useErrorHandling';

interface MonitoringDashboardProps {
  autoRefresh?: boolean;
  refreshInterval?: number;
  showAdvancedMetrics?: boolean;
}

interface MetricCard {
  title: string;
  value: string | number;
  status: 'good' | 'warning' | 'critical';
  trend?: 'up' | 'down' | 'stable';
  icon: React.ReactNode;
  description?: string;
}

export const MonitoringDashboard: React.FC<MonitoringDashboardProps> = ({
  autoRefresh = true,
  refreshInterval = 5000,
  showAdvancedMetrics = false
}) => {
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState<string | null>(null);
  const [historicalData, setHistoricalData] = useState<Array<{
    timestamp: number;
    lcp: number;
    fid: number;
    cls: number;
    memory: number;
  }>>([]);

  const { getErrorMetrics } = useErrorHandling();

  // Refresh metrics
  const refreshMetrics = async () => {
    setIsLoading(true);
    try {
      const monitor = getPerformanceMonitor();
      if (monitor) {
        const currentMetrics = monitor.getMetrics();
        setMetrics(currentMetrics);
        
        // Add to historical data
        setHistoricalData(prev => {
          const newDataPoint = {
            timestamp: Date.now(),
            lcp: currentMetrics.coreWebVitals.lcp || 0,
            fid: currentMetrics.coreWebVitals.fid || 0,
            cls: currentMetrics.coreWebVitals.cls || 0,
            memory: currentMetrics.memory.usedHeapSize / 1024 / 1024 // MB
          };
          
          // Keep last 50 data points
          const updated = [...prev, newDataPoint].slice(-50);
          return updated;
        });
      }
    } catch (error) {
      console.error('Failed to refresh metrics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Auto refresh
  useEffect(() => {
    refreshMetrics();
    
    if (autoRefresh) {
      const interval = setInterval(refreshMetrics, refreshInterval);
      return () => clearInterval(interval);
    }
    return () => {}; // Return empty cleanup function when autoRefresh is false
  }, [autoRefresh, refreshInterval]);

  // Core Web Vitals status
  const getWebVitalStatus = (metric: 'lcp' | 'fid' | 'cls', value: number | null): 'good' | 'warning' | 'critical' => {
    if (value === null) return 'warning';
    
    const thresholds = {
      lcp: { good: 2500, warning: 4000 },
      fid: { good: 100, warning: 300 },
      cls: { good: 0.1, warning: 0.25 }
    };
    
    const threshold = thresholds[metric];
    if (value <= threshold.good) return 'good';
    if (value <= threshold.warning) return 'warning';
    return 'critical';
  };

  // Memory usage status
  const getMemoryStatus = (used: number, total: number): 'good' | 'warning' | 'critical' => {
    const usage = used / total;
    if (usage < 0.6) return 'good';
    if (usage < 0.8) return 'warning';
    return 'critical';
  };

  // Metric cards configuration
  const metricCards: MetricCard[] = useMemo(() => {
    if (!metrics) return [];

    const errorMetrics = getErrorMetrics();
    
    return [
      {
        title: 'Largest Contentful Paint',
        value: metrics.coreWebVitals.lcp ? `${Math.round(metrics.coreWebVitals.lcp)}ms` : 'N/A',
        status: getWebVitalStatus('lcp', metrics.coreWebVitals.lcp),
        icon: <IconClock size={20} />,
        description: 'Time until largest content element is rendered'
      },
      {
        title: 'First Input Delay',
        value: metrics.coreWebVitals.fid ? `${Math.round(metrics.coreWebVitals.fid)}ms` : 'N/A',
        status: getWebVitalStatus('fid', metrics.coreWebVitals.fid),
        icon: <IconActivity size={20} />,
        description: 'Time between user interaction and browser response'
      },
      {
        title: 'Cumulative Layout Shift',
        value: metrics.coreWebVitals.cls ? metrics.coreWebVitals.cls.toFixed(3) : 'N/A',
        status: getWebVitalStatus('cls', metrics.coreWebVitals.cls),
        icon: <IconEye size={20} />,
        description: 'Visual stability score'
      },
      {
        title: 'Memory Usage',
        value: `${Math.round(metrics.memory.usedHeapSize / 1024 / 1024)}MB`,
        status: getMemoryStatus(metrics.memory.usedHeapSize, metrics.memory.heapSizeLimit),
        icon: <IconCpu size={20} />,
        description: `${Math.round((metrics.memory.usedHeapSize / metrics.memory.heapSizeLimit) * 100)}% of heap limit`
      },
      {
        title: 'API Response Time',
        value: `${Math.round(metrics.api.averageResponseTime)}ms`,
        status: metrics.api.averageResponseTime < 1000 ? 'good' : 
                metrics.api.averageResponseTime < 3000 ? 'warning' : 'critical',
        icon: <IconNetwork size={20} />,
        description: `${metrics.api.totalRequests} total requests`
      },
      {
        title: 'Error Rate',
        value: `${(metrics.api.errorRate * 100).toFixed(2)}%`,
        status: metrics.api.errorRate < 0.01 ? 'good' : 
                metrics.api.errorRate < 0.05 ? 'warning' : 'critical',
        icon: <IconBug size={20} />,
        description: `${errorMetrics.totalErrors} total errors`
      },
      {
        title: 'User Interactions',
        value: metrics.interactions.clickEvents + metrics.interactions.scrollEvents,
        status: 'good',
        icon: <IconUsers size={20} />,
        description: 'Total user interactions'
      },
      {
        title: 'Resource Count',
        value: metrics.resources.resourceCount,
        status: metrics.resources.resourceCount < 50 ? 'good' : 
                metrics.resources.resourceCount < 100 ? 'warning' : 'critical',
        icon: <IconDatabase size={20} />,
        description: `${Math.round(metrics.resources.totalSize / 1024 / 1024)}MB total size`
      }
    ];
  }, [metrics, getErrorMetrics]);

  // Chart data
  const chartData = historicalData.map((point, index) => ({
    time: new Date(point.timestamp).toLocaleTimeString(),
    'LCP (ms)': point.lcp,
    'Memory (MB)': point.memory
  }));

  const exportMetrics = () => {
    if (!metrics) return;
    
    const logger = getLogger();
    const data = {
      metrics,
      errorMetrics: getErrorMetrics(),
      timestamp: new Date().toISOString(),
      logs: logger.exportLogs()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { 
      type: 'application/json' 
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `monitoring-report-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getStatusColor = (status: 'good' | 'warning' | 'critical') => {
    switch (status) {
      case 'good': return 'green';
      case 'warning': return 'yellow';
      case 'critical': return 'red';
      default: return 'gray';
    }
  };

  const getStatusIcon = (status: 'good' | 'warning' | 'critical') => {
    switch (status) {
      case 'critical': return <IconAlertTriangle size={16} />;
      default: return null;
    }
  };

  if (isLoading && !metrics) {
    return (
      <Card withBorder p="xl" radius="md">
        <Group justify="center">
          <Text>Loading monitoring data...</Text>
        </Group>
      </Card>
    );
  }

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <Text size="xl" fw={600}>System Monitoring</Text>
        <Group gap="sm">
          <Tooltip label="Refresh metrics">
            <ActionIcon
              variant="subtle"
              onClick={refreshMetrics}
              loading={isLoading}
            >
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Export report">
            <ActionIcon variant="subtle" onClick={exportMetrics}>
              <IconDownload size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {/* Critical alerts */}
      {metricCards.some(card => card.status === 'critical') && (
        <Alert
          icon={<IconAlertTriangle />}
          title="Critical Performance Issues Detected"
          color="red"
          variant="light"
        >
          {metricCards
            .filter(card => card.status === 'critical')
            .map(card => card.title)
            .join(', ')} require immediate attention.
        </Alert>
      )}

      {/* Metric cards */}
      <Grid>
        {metricCards.map((card, index) => (
          <Grid.Col key={index} span={{ base: 12, sm: 6, lg: 3 }}>
            <Card
              withBorder
              padding="md"
              radius="md"
              style={{
                cursor: 'pointer',
                borderColor: card.status === 'critical' ? 'var(--mantine-color-red-4)' : undefined
              }}
              onClick={() => setSelectedMetric(card.title)}
            >
              <Group justify="space-between">
                <Group gap="sm">
                  {card.icon}
                  <Stack gap={0}>
                    <Text size="sm" c="dimmed">{card.title}</Text>
                    <Text size="lg" fw={600}>{card.value}</Text>
                  </Stack>
                </Group>
                <Group gap="xs">
                  <Badge 
                    color={getStatusColor(card.status)} 
                    variant="light"
                    size="sm"
                  >
                    {card.status}
                  </Badge>
                  {getStatusIcon(card.status)}
                </Group>
              </Group>
              {card.description && (
                <Text size="xs" c="dimmed" mt="xs">
                  {card.description}
                </Text>
              )}
            </Card>
          </Grid.Col>
        ))}
      </Grid>

      {/* Charts */}
      {historicalData.length > 0 && (
        <Card withBorder padding="lg" radius="md">
          <Text size="lg" fw={500} mb="md">Performance Trends</Text>
{/* LineChart requires @mantine/charts - using placeholder */}
          <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--mantine-color-gray-1)' }}>
            <Text c="dimmed">Chart visualization requires @mantine/charts</Text>
          </div>
        </Card>
      )}

      {/* Advanced metrics */}
      {showAdvancedMetrics && metrics && (
        <Tabs defaultValue="resources">
          <Tabs.List>
            <Tabs.Tab value="resources" leftSection={<IconDatabase size={16} />}>
              Resources
            </Tabs.Tab>
            <Tabs.Tab value="interactions" leftSection={<IconUsers size={16} />}>
              Interactions
            </Tabs.Tab>
            <Tabs.Tab value="navigation" leftSection={<IconActivity size={16} />}>
              Navigation
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="resources" pt="md">
            <Card withBorder padding="lg" radius="md">
              <Stack gap="md">
                <Text size="lg" fw={500}>Resource Usage</Text>
                <Grid>
                  <Grid.Col span={6}>
                    <RingProgress
                      size={120}
                      thickness={12}
                      sections={[
                        { value: (metrics.resources.jsSize / metrics.resources.totalSize) * 100, color: 'blue' },
                        { value: (metrics.resources.cssSize / metrics.resources.totalSize) * 100, color: 'green' },
                        { value: (metrics.resources.imageSize / metrics.resources.totalSize) * 100, color: 'orange' }
                      ]}
                      label={
                        <Text size="xs" ta="center">
                          {Math.round(metrics.resources.totalSize / 1024 / 1024)}MB
                        </Text>
                      }
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Stack gap="sm">
                      <Group justify="space-between">
                        <Badge color="blue">JavaScript</Badge>
                        <Text size="sm">{Math.round(metrics.resources.jsSize / 1024)}KB</Text>
                      </Group>
                      <Group justify="space-between">
                        <Badge color="green">CSS</Badge>
                        <Text size="sm">{Math.round(metrics.resources.cssSize / 1024)}KB</Text>
                      </Group>
                      <Group justify="space-between">
                        <Badge color="orange">Images</Badge>
                        <Text size="sm">{Math.round(metrics.resources.imageSize / 1024)}KB</Text>
                      </Group>
                    </Stack>
                  </Grid.Col>
                </Grid>
              </Stack>
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="interactions" pt="md">
            <Card withBorder padding="lg" radius="md">
              <Text size="lg" fw={500} mb="md">User Interactions</Text>
{/* BarChart requires @mantine/charts - using placeholder */}
              <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--mantine-color-gray-1)' }}>
                <Text c="dimmed">Chart visualization requires @mantine/charts</Text>
              </div>
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="navigation" pt="md">
            <Card withBorder padding="lg" radius="md">
              <Text size="lg" fw={500} mb="md">Navigation Timing</Text>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text size="sm">Load Time</Text>
                  <Badge>{Math.round(metrics.navigation.loadTime)}ms</Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">DOM Content Loaded</Text>
                  <Badge>{Math.round(metrics.navigation.domContentLoaded)}ms</Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">First Paint</Text>
                  <Badge>{Math.round(metrics.navigation.firstPaint)}ms</Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">First Contentful Paint</Text>
                  <Badge>{Math.round(metrics.navigation.firstContentfulPaint)}ms</Badge>
                </Group>
              </Stack>
            </Card>
          </Tabs.Panel>
        </Tabs>
      )}

      {/* Metric detail modal */}
      <Modal
        opened={!!selectedMetric}
        onClose={() => setSelectedMetric(null)}
        title={`${selectedMetric} Details`}
        size="lg"
      >
        {selectedMetric && metrics && (
          <Stack gap="md">
            <JsonInput
              label="Raw Metrics Data"
              value={JSON.stringify(metrics, null, 2)}
              readOnly
              minRows={10}
              maxRows={20}
            />
            <Group justify="flex-end">
              <Button
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(metrics, null, 2));
                }}
              >
                Copy to Clipboard
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
};